import { useState } from "react";
import axios from "axios";
import { SendHorizonal } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { linkedinUrl, services } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { name: "", email: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, company: form.company || null, service: form.service || null });
      toast.success("Votre demande a bien été envoyée.");
      setForm(initialForm);
    } catch (error) {
      toast.error("L’envoi a échoué. Vérifiez les champs puis réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="contact-page">
      <SEO title="Contact" description="Contactez Intellix pour un accompagnement en transformation numérique, licences Microsoft, Copilot, Purview, SOC et hébergement souverain. Cabinet basé à Nantes." path="/contact" />
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.85fr_1.15fr]">
        <Reveal>
          <SectionHeading
            eyebrow="Contact"
            title="Parlons de votre prochaine étape numérique."
            description="Décrivez votre contexte et vos priorités : Intellix vous répondra avec une première orientation claire."
          />
          <div className="mt-10 rounded-3xl border border-border/70 bg-card/60 p-7" data-testid="contact-info-panel">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">Intellix</p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Transformation numérique des PME, technologies Microsoft, sécurité Cloud, IA générative et politique Zero Trust.
            </p>
            <a href={linkedinUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex text-sm font-semibold text-primary transition-colors hover:text-foreground" data-testid="contact-linkedin-link">
              Voir la page LinkedIn
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <form onSubmit={submit} className="tracing-beam rounded-[2rem] bg-card p-6 sm:p-8" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="grid gap-2 text-sm font-medium">
                Nom complet
                <Input name="name" value={form.name} onChange={updateField} required minLength={2} placeholder="Votre nom" className="h-12 rounded-xl" data-testid="contact-name-input" />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Adresse e-mail
                <Input name="email" type="email" value={form.email} onChange={updateField} required placeholder="vous@entreprise.fr" className="h-12 rounded-xl" data-testid="contact-email-input" />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Entreprise
                <Input name="company" value={form.company} onChange={updateField} placeholder="Nom de votre PME" className="h-12 rounded-xl" data-testid="contact-company-input" />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Sujet prioritaire
                <select name="service" value={form.service} onChange={updateField} className="h-12 rounded-xl border border-input bg-background px-3 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="contact-service-select">
                  <option value="">Sélectionner un besoin</option>
                  {services.map((service) => <option key={service.title} value={service.title}>{service.title}</option>)}
                </select>
              </label>
            </div>
            <label className="mt-5 grid gap-2 text-sm font-medium">
              Votre message
              <Textarea name="message" value={form.message} onChange={updateField} required minLength={10} placeholder="Parlez-nous de vos enjeux, outils actuels et échéances." className="min-h-44 rounded-xl" data-testid="contact-message-textarea" />
            </label>
            <Button type="submit" disabled={submitting} className="mt-7 h-12 rounded-full px-8 font-semibold" data-testid="contact-submit-button">
              {submitting ? "Envoi en cours..." : "Envoyer la demande"} <SendHorizonal className="h-4 w-4" />
            </Button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
